import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, X, Ban, IndianRupee, Move, Trash2, Pencil, Plus, Minus, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useMasterData } from '../contexts/MasterDataContext';
import { API } from '../utils/apiConfig';

const ROW_H = 36;
const COL_W = 116;
const TIME_W = 54;
// Grid frame is now dynamic per-day (min open → max close across venue courts).
// The backend returns `cal.start_min` / `cal.end_min`; the fallback is 6 AM.
const DEFAULT_START = 360;
const STEP = 30;

const minToLabel = (min) => {
  const m = ((min % 1440) + 1440) % 1440;
  let h = Math.floor(m / 60);
  const mm = m % 60;
  const p = h < 12 ? 'AM' : 'PM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return mm === 0 ? `${h12} ${p}` : `${h12}:${String(mm).padStart(2, '0')} ${p}`;
};
const minToApi = (min) => {
  const m = ((min % 1440) + 1440) % 1440;
  let h = Math.floor(m / 60);
  const mm = m % 60;
  const p = h < 12 ? 'AM' : 'PM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${String(h12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${p}`;
};

const SOURCE_COLORS = {
  khelomore: 'bg-emerald-600 border-emerald-700',
  district: 'bg-sky-600 border-sky-700',
  hudle: 'bg-orange-500 border-orange-600',
  'walk-in': 'bg-slate-600 border-slate-700',
  calling: 'bg-teal-600 border-teal-700',
  whatsapp: 'bg-green-600 border-green-700',
  online: 'bg-indigo-600 border-indigo-700',
  app: 'bg-indigo-600 border-indigo-700',
};
const srcColor = (src) => SOURCE_COLORS[String(src || '').toLowerCase()] || 'bg-violet-600 border-violet-700';

const SOURCES = ['Calling', 'WhatsApp', 'Khelomore', 'District', 'Hudle', 'Online'];
const PAY_MODES = ['Cash', 'UPI', 'Card', 'Pending'];
const BLOCK_REASONS = ['Offline', 'Maintenance', 'Coaching', 'Cleaning', 'Event', 'Private Booking', 'District', 'Khelomore', 'Hudle', 'Other'];

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Something went wrong';
  if (typeof d === 'string') return d;
  if (d.conflict) return `${d.message} — held by ${d.conflict.player_name || 'another booking'} (${d.conflict.start_time || ''}–${d.conflict.end_time || ''})`;
  return d.message || JSON.stringify(d);
};

export default function SlotBookingPage() {
  const { admin } = useAuth();
  const { venues } = useMasterData();
  const navigate = useNavigate();

  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [venue, setVenue] = useState(localStorage.getItem('slotcal_venue') || '');
  const [game, setGame] = useState('all');
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({}); // { courtName: {start, end} } inclusive slot indices
  const [detail, setDetail] = useState(null); // {type:'booking'|'block', data}
  const [moveBooking, setMoveBooking] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bookForm, setBookForm] = useState({ name: '', mobile: '', source: 'Calling', payMode: 'Cash', amount: '', advance: '' });
  const [bookGames, setBookGames] = useState({}); // { [courtName]: gameName }
  const [blockForm, setBlockForm] = useState({ reason: 'Offline', notes: '' });
  const [editBlock, setEditBlock] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [lastBooking, setLastBooking] = useState(null);
  const suggestTimer = useRef(null);
  const dragRef = useRef(null);
  const didDragRef = useRef(false);
  const scrollRef = useRef(null);

  const activeVenues = useMemo(() => (venues || []).filter((v) => v.is_active !== false), [venues]);

  useEffect(() => {
    if (!venue && activeVenues.length) setVenue(activeVenues[0].name);
  }, [activeVenues, venue]);

  const fetchCal = useCallback(async (quiet = false) => {
    if (!venue) return;
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get(`${API}/slot-calendar`, { params: { date, venue } });
      setCal(res.data);
      const games = [...new Set(res.data.courts.flatMap((c) => c.games))];
      setGame((g) => (g === 'all' || games.includes(g) ? g : 'all'));
    } catch (e) {
      if (!quiet) toast.error(errMsg(e));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [date, venue]);

  const fetchCalRef = useRef(fetchCal);
  useEffect(() => { fetchCalRef.current = fetchCal; }, [fetchCal]);
  const stateRef = useRef({ date, venue });
  useEffect(() => { stateRef.current = { date, venue }; }, [date, venue]);

  useEffect(() => {
    setSel({});
    setMoveBooking(null);
    fetchCal();
  }, [fetchCal]);

  // ---- real-time sync: SSE stream + 30s polling fallback ----
  useEffect(() => {
    const token = localStorage.getItem('token');
    let es = null;
    let retryTimer = null;
    let debTimer = null;
    const refetch = () => {
      clearTimeout(debTimer);
      debTimer = setTimeout(() => fetchCalRef.current(true), 400);
    };
    const connect = () => {
      if (!token) return;
      es = new EventSource(`${API}/slot-calendar/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => {
        try {
          const e = JSON.parse(ev.data);
          const { date: d, venue: v } = stateRef.current;
          if ((!e.venue || e.venue === v) && (!e.date || e.date === d)) refetch();
        } catch { /* ignore */ }
      };
      es.onerror = () => {
        es.close();
        retryTimer = setTimeout(connect, 5000);
      };
    };
    connect();
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') fetchCalRef.current(true);
    }, 30000);
    return () => {
      if (es) es.close();
      clearTimeout(retryTimer);
      clearTimeout(debTimer);
      clearInterval(poll);
    };
  }, []);

  useEffect(() => {
    if (venue) localStorage.setItem('slotcal_venue', venue);
  }, [venue]);

  const gridStart = cal?.start_min ?? DEFAULT_START;
  const nSlots = cal ? (cal.end_min - cal.start_min) / STEP : 0;
  const slotMin = (i) => gridStart + i * STEP;

  // ---- per-court slot status ----
  const courtStatus = useMemo(() => {
    if (!cal) return {};
    const map = {};
    for (const c of cal.courts) {
      const arr = new Array(nSlots).fill(null); // null=free, else {type, ref}
      if (c.bounds) {
        const [o, cl] = c.bounds;
        for (let i = 0; i < nSlots; i++) {
          const cs = slotMin(i) % 1440;
          const working = (o <= cs && cs < cl) || (o <= cs + 1440 && cs + 1440 < cl);
          if (!working) arr[i] = { type: 'closed' };
        }
      } else {
        for (let i = 0; i < nSlots; i++) arr[i] = { type: 'closed' };
      }
      map[c.court] = arr;
    }
    for (const b of cal.blocks) {
      const arr = map[b.court];
      if (!arr) continue;
      const s = Math.max(0, (b.start_min - gridStart) / STEP);
      const e = Math.min(nSlots, (b.end_min - gridStart) / STEP);
      for (let i = Math.floor(s); i < e; i++) arr[i] = { type: 'blocked', ref: b };
    }
    for (const b of cal.bookings) {
      const arr = map[b.court];
      if (!arr) continue;
      const s = Math.max(0, (b.start_min - gridStart) / STEP);
      const e = Math.min(nSlots, (b.end_min - gridStart) / STEP);
      for (let i = Math.floor(s); i < e; i++) arr[i] = { type: 'booked', ref: b };
    }
    return map;
  }, [cal, nSlots]);

  const isFree = (court, i, ignoreBookingId = null) => {
    const s = courtStatus[court]?.[i];
    if (!s) return true;
    if (ignoreBookingId && s.type === 'booked' && s.ref?.id === ignoreBookingId) return true;
    return false;
  };

  // Drop selected slots that just became unavailable (real-time refresh)
  useEffect(() => {
    setSel((prev) => {
      const invalid = [];
      const next = {};
      for (const [ct, r] of Object.entries(prev)) {
        const status = courtStatus[ct];
        let ok = !!status;
        if (ok) {
          for (let i = r.start; i <= r.end; i++) {
            if (status[i]) { ok = false; break; }
          }
        }
        if (ok) next[ct] = r;
        else invalid.push(ct);
      }
      if (!invalid.length) return prev;
      invalid.forEach((ct) => {
        const r = prev[ct];
        toast.error(`${ct} · ${minToLabel(gridStart + r.start * STEP)}–${minToLabel(gridStart + (r.end + 1) * STEP)} was just booked/blocked — removed from your selection`, { id: `sel-removed-${ct}`, duration: 6000 });
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtStatus]);

  const priceArr = (c) => c.prices[game] || c.prices[c.games[0]] || [];
  const rangePrice = (court, r) => {
    const c = cal?.courts.find((x) => x.court === court);
    if (!c || !r) return 0;
    const pa = priceArr(c);
    let sum = 0;
    for (let i = r.start; i <= r.end; i++) sum += pa[i] || 0;
    return Math.round(sum * 100) / 100;
  };
  const totalPrice = useMemo(
    () => Object.entries(sel).reduce((t, [ct, r]) => t + rangePrice(ct, r), 0),
    [sel, cal, game] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const selCount = Object.keys(sel).length;

  // ---- selection interactions ----
  const clampRange = (court, anchor, target) => {
    const dir = target >= anchor ? 1 : -1;
    let end = anchor;
    for (let i = anchor + dir; dir > 0 ? i <= target : i >= target; i += dir) {
      if (!isFree(court, i)) break;
      end = i;
    }
    return { start: Math.min(anchor, end), end: Math.max(anchor, end) };
  };

  const handleCellMouseDown = (court, i, e) => {
    if (e.button !== 0 || moveBooking) return;
    dragRef.current = { court, anchor: i };
    didDragRef.current = false;
  };
  const handleCellMouseEnter = (court, i) => {
    const d = dragRef.current;
    if (!d || d.court !== court) return;
    didDragRef.current = true;
    setSel((prev) => ({ ...prev, [court]: clampRange(court, d.anchor, i) }));
  };
  useEffect(() => {
    const up = () => { dragRef.current = null; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const doMove = async (court, i) => {
    const b = moveBooking;
    const slots = (b.end_min - b.start_min) / STEP;
    for (let k = i; k < i + slots; k++) {
      if (k >= nSlots || !isFree(court, k, b.id)) {
        toast.error('Not enough free space at that time');
        return;
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/bookings/${b.id}`, {
        court,
        start_time: minToApi(slotMin(i)),
        end_time: minToApi(slotMin(i) + slots * STEP),
      });
      toast.success(`Moved to ${court}, ${minToLabel(slotMin(i))}`);
      setMoveBooking(null);
      fetchCal();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handleCellClick = (court, i) => {
    if (didDragRef.current) { didDragRef.current = false; return; }
    if (moveBooking) { doMove(court, i); return; }
    if (!isFree(court, i)) return;
    setSel((prev) => {
      const r = prev[court];
      const next = { ...prev };
      if (!r) next[court] = { start: i, end: i };
      else if (i === r.end + 1) next[court] = { ...r, end: i };
      else if (i === r.start - 1) next[court] = { ...r, start: i };
      else if (i >= r.start && i <= r.end) delete next[court];
      else next[court] = { start: i, end: i };
      return next;
    });
  };

  // ---- booking dialog ----
  const openBook = () => {
    setBookForm((f) => ({ ...f, amount: String(totalPrice), advance: '' }));
    const initGames = {};
    for (const ct of Object.keys(sel)) {
      const courtObj = cal?.courts.find((c) => c.court === ct);
      const games = courtObj?.games || [];
      initGames[ct] = games.includes(game) ? game : (games[0] || '');
    }
    setBookGames(initGames);
    setSuggestions([]);
    setLastBooking(null);
    setBookOpen(true);
  };

  const submitBooking = async () => {
    if (!bookForm.name.trim()) { toast.error('Player name is required'); return; }
    const entries = Object.entries(sel);
    if (!entries.length) { toast.error('Selected slots are no longer available'); setBookOpen(false); return; }
    const computed = entries.map(([ct, r]) => rangePrice(ct, r));
    const compTotal = computed.reduce((a, b) => a + b, 0);
    const userAmt = parseFloat(bookForm.amount || '0') || 0;
    const userAdv = parseFloat(bookForm.advance || '0') || 0;
    setSaving(true);
    let ok = 0;
    for (let idx = 0; idx < entries.length; idx++) {
      const [ct, r] = entries[idx];
      const share = compTotal > 0 ? computed[idx] / compTotal : 1 / entries.length;
      const amt = Math.round(userAmt * share * 100) / 100;
      const adv = Math.round(userAdv * share * 100) / 100;
      const courtObj = cal.courts.find((c) => c.court === ct);
      const chosen = bookGames[ct];
      const g = courtObj?.games.includes(chosen) ? chosen : (courtObj?.games[0] || chosen || game);
      try {
        await axios.post(`${API}/bookings`, {
          venue,
          game: g,
          court: ct,
          date,
          start_time: minToApi(slotMin(r.start)),
          end_time: minToApi(slotMin(r.end) + STEP),
          player_name: bookForm.name.trim(),
          player_mobile: bookForm.mobile.trim() || 'N/A',
          amount: amt,
          advance_payment: adv,
          pending_amount: Math.max(0, Math.round((amt - adv) * 100) / 100),
          payment_mode: bookForm.payMode,
          booking_source: bookForm.source,
        });
        ok += 1;
      } catch (e) {
        toast.error(`${ct}: ${errMsg(e)}`);
      }
    }
    setSaving(false);
    if (ok > 0) {
      toast.success(`${ok} booking${ok > 1 ? 's' : ''} created`);
      setBookOpen(false);
      setSel({});
      setBookForm({ name: '', mobile: '', source: 'Calling', payMode: 'Cash', amount: '', advance: '' });
      fetchCal();
    }
  };

  // ---- block dialog ----
  const submitBlock = async () => {
    const dow = (new Date(date + 'T00:00:00').getDay() + 6) % 7; // Mon=0
    setSaving(true);
    let ok = 0;
    for (const [ct, r] of Object.entries(sel)) {
      try {
        await axios.post(`${API}/slot-blocks`, {
          venue,
          court: ct,
          game: '',
          reason: blockForm.reason,
          start_date: date,
          end_date: date,
          start_time: minToApi(slotMin(r.start)),
          end_time: minToApi(slotMin(r.end) + STEP),
          days_of_week: [dow],
          continue_monthly: false,
          repeat_type: 'none',
          pricing_type: 'fixed_monthly',
          monthly_amount: 0,
          hourly_rate: 0,
          notes: blockForm.notes,
          instructed_by: '',
          entered_by: admin?.name || admin?.email || '',
        });
        ok += 1;
      } catch (e) {
        toast.error(`${ct}: ${errMsg(e)}`);
      }
    }
    setSaving(false);
    if (ok > 0) {
      toast.success(`${ok} slot block${ok > 1 ? 's' : ''} created`);
      setBlockOpen(false);
      setSel({});
      setBlockForm({ reason: 'Offline', notes: '' });
      fetchCal();
    }
  };

  // ---- booking actions ----
  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel booking for ${b.player_name}?`)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/bookings/${b.id}/cancel`, { reason: 'Cancelled from Slot Booking calendar' });
      toast.success('Booking cancelled');
      setDetail(null);
      fetchCal();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const resizeBooking = async (b, delta) => {
    const newEnd = b.end_min + delta;
    if (newEnd <= b.start_min) { toast.error('Booking must be at least 30 minutes'); return; }
    if (delta > 0) {
      const i = (b.end_min - gridStart) / STEP;
      if (i >= nSlots || !isFree(b.court, i, b.id)) { toast.error('Next slot is not free'); return; }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/bookings/${b.id}`, { end_time: minToApi(newEnd) });
      toast.success(`End time → ${minToLabel(newEnd)}`);
      setDetail(null);
      fetchCal();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const cancelFreeSlot = async (blk) => {
    const label = `${minToLabel(blk.start_min)}–${minToLabel(blk.end_min)} on ${format(new Date(date + 'T00:00:00'), 'd MMM yyyy')}`;
    if (!window.confirm(`Free this bulk slot (${label})?\n\nOnly this occurrence is cancelled — the bulk booking continues on all other days. The slot becomes available for normal booking.`)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/slot-blocks/${blk.id}/cancel-occurrence`, { date });
      toast.success('Slot freed — now available for booking');
      setDetail(null);
      fetchCal();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteBlock = async (blk) => {
    if (!window.confirm(`Remove block "${blk.reason}"?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/slot-blocks/${blk.id}`);
      toast.success('Block removed');
      setDetail(null);
      fetchCal();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const saveBlockEdit = async () => {
    const b = editBlock;
    if (b.end_min <= b.start_min) { toast.error('End time must be after start time'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/slot-blocks/${b.id}`, {
        reason: b.reason,
        notes: b.notes,
        start_time: minToApi(b.start_min),
        end_time: minToApi(b.end_min),
      });
      toast.success('Block updated');
      setEditBlock(null);
      fetchCal();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const timeOptions = useMemo(() => {
    const out = [];
    for (let m = gridStart; m <= (cal?.end_min || 1500); m += STEP) out.push(m);
    return out;
  }, [cal]);

  // ---- date strip ----
  const days = useMemo(() => {
    const out = [];
    for (let i = -1; i < 14; i++) out.push(addDays(new Date(), i));
    return out;
  }, []);
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const now = new Date();
  let nowMin = now.getHours() * 60 + now.getMinutes();
  if (nowMin < gridStart) nowMin += 1440;

  const games = useMemo(() => (cal ? [...new Set(cal.courts.flatMap((c) => c.games))] : []), [cal]);
  const visibleCourts = useMemo(
    () => (cal ? (game === 'all' ? cal.courts : cal.courts.filter((c) => c.games.includes(game))) : []),
    [cal, game]
  );

  const handleNameChange = (value) => {
    setBookForm((f) => ({ ...f, name: value }));
    setLastBooking(null);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (value.trim().length < 2) { setSuggestions([]); return; }
    suggestTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/slot-calendar/player-search`, { params: { q: value.trim() } });
        setSuggestions(res.data.results || []);
      } catch { setSuggestions([]); }
    }, 300);
  };

  const handleMobileChange = (value) => {
    setBookForm((f) => ({ ...f, mobile: value }));
    setLastBooking(null);
    const digits = value.replace(/\D/g, '');
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (digits.length < 4) { setSuggestions([]); return; }
    suggestTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/slot-calendar/player-search`, { params: { q: digits } });
        setSuggestions(res.data.results || []);
      } catch { setSuggestions([]); }
    }, 300);
  };

  return (
    <DashboardLayout>
      <div className="space-y-3" data-testid="slot-booking-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <CalendarRange className="w-6 h-6 text-emerald-600" /> Slot Booking
          </h1>
          <div className="ml-auto flex items-center gap-2">
            {games.length > 1 && (
              <Select value={game} onValueChange={(v) => { setGame(v); setSel({}); }}>
                <SelectTrigger className="w-[130px] h-9" data-testid="slotcal-game-select">
                  <SelectValue placeholder="Game" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Games</SelectItem>
                  {games.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => fetchCal()} data-testid="slotcal-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Venue pill buttons */}
        {activeVenues.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="venue-pills">
            {activeVenues.map((v, idx) => {
              const PILL_COLORS = [
                'bg-emerald-600 text-white hover:bg-emerald-700',
                'bg-sky-600 text-white hover:bg-sky-700',
                'bg-violet-600 text-white hover:bg-violet-700',
                'bg-amber-500 text-white hover:bg-amber-600',
                'bg-rose-600 text-white hover:bg-rose-700',
                'bg-teal-600 text-white hover:bg-teal-700',
                'bg-indigo-600 text-white hover:bg-indigo-700',
                'bg-orange-500 text-white hover:bg-orange-600',
              ];
              const PILL_INACTIVE = [
                'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-300',
                'bg-sky-100 text-sky-700 hover:bg-sky-200 border border-sky-300',
                'bg-violet-100 text-violet-700 hover:bg-violet-200 border border-violet-300',
                'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300',
                'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-300',
                'bg-teal-100 text-teal-700 hover:bg-teal-200 border border-teal-300',
                'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border border-indigo-300',
                'bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-300',
              ];
              const colorIdx = idx % PILL_COLORS.length;
              const isActive = venue === v.name;
              return (
                <button
                  key={v.id}
                  onClick={() => setVenue(v.name)}
                  data-testid={`venue-pill-${v.name.toLowerCase().replace(/\s+/g, '-')}`}
                  aria-pressed={isActive}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? `${PILL_COLORS[colorIdx]} shadow-md ring-2 ring-offset-1 ring-slate-300 opacity-100`
                      : 'bg-slate-100 text-slate-500 border border-slate-200 opacity-70 hover:opacity-100 hover:text-slate-700'
                  }`}
                >
                  {v.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Date strip */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 overflow-x-auto pb-1 flex-1" data-testid="slotcal-date-strip">
            {days.map((d) => {
              const ds = format(d, 'yyyy-MM-dd');
              const active = ds === date;
              return (
                <button
                  key={ds}
                  onClick={() => setDate(ds)}
                  data-testid={`slotcal-date-${ds}`}
                  className={`flex flex-col items-center px-3 py-1.5 rounded-lg border text-xs shrink-0 transition-colors ${
                    active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white hover:bg-emerald-50 border-slate-200'
                  }`}
                >
                  <span className="font-semibold">{format(d, 'EEE')}</span>
                  <span className={active ? 'text-emerald-100' : 'text-slate-500'}>{format(d, 'd MMM')}</span>
                </button>
              );
            })}
          </div>
          <Input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="w-[130px] h-9 shrink-0"
            data-testid="slotcal-date-input"
          />
        </div>

        {/* Legend + hint */}
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-white border border-slate-300 inline-block" /> Vacant</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-indigo-600 inline-block" /> Booked</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-400 inline-block" /> Blocked</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-800 inline-block" /> Closed</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-200 border border-emerald-500 inline-block" /> Selected</span>
          <span className="hidden sm:inline text-slate-400">· Drag or tap slots to select, tap a booking for actions</span>
        </div>

        {moveBooking && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-3 py-2 text-sm" data-testid="slotcal-move-banner">
            <Move className="w-4 h-4" />
            Moving <b>{moveBooking.player_name}</b> ({minToLabel(moveBooking.start_min)}–{minToLabel(moveBooking.end_min)}) — tap a vacant slot
            <Button variant="ghost" size="sm" className="ml-auto h-7" onClick={() => setMoveBooking(null)}>Cancel</Button>
          </div>
        )}

        {/* Grid */}
        <div ref={scrollRef} className="overflow-auto border rounded-xl bg-white select-none" style={{ maxHeight: 'calc(100vh - 235px)' }} data-testid="slotcal-grid">
          {cal && (
            <div style={{ display: 'flex', minWidth: TIME_W + visibleCourts.length * COL_W }}>
              {/* time column */}
              <div className="sticky left-0 z-20 bg-white border-r" style={{ width: TIME_W, flex: '0 0 auto' }}>
                <div className="sticky top-0 z-30 bg-slate-50 border-b flex items-center justify-center text-[10px] font-semibold text-slate-500" style={{ height: 36 }}>
                  Time
                </div>
                {Array.from({ length: nSlots }).map((_, i) => (
                  <div
                    key={i}
                    className={`text-[11px] font-bold text-slate-700 text-right pr-1.5 border-b ${slotMin(i) % 60 === 30 ? 'border-slate-400' : 'border-slate-300/70'} ${i % 2 === 0 ? 'bg-white' : 'bg-slate-100/70'}`}
                    style={{ height: ROW_H, lineHeight: `${ROW_H}px` }}
                  >
                    {slotMin(i) % 60 === 0 ? minToLabel(slotMin(i)) : ''}
                  </div>
                ))}
              </div>
              {/* court columns */}
              {visibleCourts.map((c) => {
                const status = courtStatus[c.court] || [];
                const r = sel[c.court];
                const pa = priceArr(c);
                return (
                  <div key={c.court_id} style={{ width: COL_W, flex: '0 0 auto' }} className="border-r border-slate-200">
                    <div className="sticky top-0 z-10 bg-slate-50 border-b flex flex-col items-center justify-center" style={{ height: 36 }}>
                      <span className="text-xs font-bold text-slate-700">{c.court}</span>
                      <span className="text-[9px] text-slate-400 truncate max-w-full px-1">{c.games.join(', ')}</span>
                    </div>
                    <div className="relative" style={{ height: nSlots * ROW_H }}>
                      {/* background cells */}
                      {Array.from({ length: nSlots }).map((_, i) => {
                        const st = status[i];
                        const isPast = date < todayStr || (date === todayStr && slotMin(i) + STEP <= nowMin);
                        const free = !st;
                        const shade = i % 2 === 0 ? 'bg-white' : 'bg-slate-100/70';
                        return (
                          <div
                            key={i}
                            data-testid={`slotcal-cell-${c.court}-${i}`}
                            onMouseDown={(e) => free && handleCellMouseDown(c.court, i, e)}
                            onMouseEnter={() => handleCellMouseEnter(c.court, i)}
                            onClick={() => handleCellClick(c.court, i)}
                            className={`border-b ${slotMin(i) % 60 === 30 ? 'border-slate-400' : 'border-slate-300/70'} ${
                              st?.type === 'closed' ? 'bg-slate-800' : free ? `${shade} hover:bg-emerald-50 cursor-pointer ${isPast ? 'opacity-60' : ''}` : shade
                            }`}
                            style={{ height: ROW_H }}
                          >
                            {free && !isPast && pa[i] > 0 && (
                              <span className="text-[8px] text-slate-300 pl-1 pointer-events-none">₹{pa[i]}</span>
                            )}
                          </div>
                        );
                      })}
                      {/* block overlays */}
                      {cal.blocks.filter((b) => b.court === c.court).map((b) => {
                        const top = Math.max(0, (b.start_min - gridStart) / STEP) * ROW_H;
                        const h = (Math.min(cal.end_min, b.end_min) - Math.max(gridStart, b.start_min)) / STEP * ROW_H;
                        if (h <= 0) return null;
                        return (
                          <div
                            key={b.id + b.start_min}
                            data-testid={`slotcal-block-${b.id}`}
                            onClick={(e) => { e.stopPropagation(); setDetail({ type: 'block', data: b }); }}
                            className="absolute inset-x-0.5 rounded bg-slate-400/90 border border-slate-500 text-white px-1.5 py-1 cursor-pointer overflow-hidden"
                            style={{ top, height: h - 2 }}
                          >
                            <div className="text-[11px] font-semibold truncate flex items-center gap-0.5"><Ban className="w-3 h-3 shrink-0" />{b.reason}</div>
                            {h > ROW_H && <div className="text-[10px] opacity-90 truncate">{minToLabel(b.start_min)}–{minToLabel(b.end_min)}</div>}
                            {h >= ROW_H * 2 - 4 && (b.blocked_by || b.notes) && (
                              <div className="text-[10px] opacity-85 truncate">{b.blocked_by ? `by ${b.blocked_by}` : ''}{b.blocked_by && b.notes ? ' · ' : ''}{b.notes}</div>
                            )}
                          </div>
                        );
                      })}
                      {/* booking overlays */}
                      {cal.bookings.filter((b) => b.court === c.court).map((b) => {
                        const top = Math.max(0, (b.start_min - gridStart) / STEP) * ROW_H;
                        const h = (Math.min(cal.end_min, b.end_min) - Math.max(gridStart, b.start_min)) / STEP * ROW_H;
                        if (h <= 0) return null;
                        return (
                          <div
                            key={b.id}
                            data-testid={`slotcal-booking-${b.id}`}
                            onClick={(e) => { e.stopPropagation(); setDetail({ type: 'booking', data: b }); }}
                            className={`absolute inset-x-0.5 rounded border text-white px-1.5 py-1 cursor-pointer overflow-hidden ${srcColor(b.source)} ${moveBooking?.id === b.id ? 'opacity-40 ring-2 ring-amber-400' : ''}`}
                            style={{ top, height: h - 2 }}
                          >
                            <div className="text-[11px] font-semibold truncate">{b.player_name}</div>
                            {h > ROW_H && <div className="text-[10px] opacity-90 truncate">{minToLabel(b.start_min)}–{minToLabel(b.end_min)}</div>}
                            {h >= ROW_H * 2 - 4 && <div className="text-[10px] opacity-80 truncate">{b.source} · ₹{b.amount}{b.booked_by ? ` · by ${b.booked_by}` : ''}</div>}
                          </div>
                        );
                      })}
                      {/* selection overlay */}
                      {r && (
                        <div
                          data-testid={`slotcal-selection-${c.court}`}
                          className="absolute inset-x-0.5 rounded bg-emerald-200/80 border-2 border-emerald-500 pointer-events-none flex flex-col items-center justify-center"
                          style={{ top: r.start * ROW_H, height: (r.end - r.start + 1) * ROW_H - 2 }}
                        >
                          <span className="text-[10px] font-bold text-emerald-900">{minToLabel(slotMin(r.start))}–{minToLabel(slotMin(r.end) + STEP)}</span>
                          <span className="text-[10px] text-emerald-800">₹{rangePrice(c.court, r)}</span>
                        </div>
                      )}
                      {/* now line */}
                      {date === todayStr && nowMin >= gridStart && nowMin <= cal.end_min && (
                        <div className="absolute inset-x-0 border-t-2 border-red-500 pointer-events-none" style={{ top: ((nowMin - gridStart) / STEP) * ROW_H }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!cal && (
            <div className="p-10 text-center text-slate-400 text-sm">{loading ? 'Loading calendar…' : 'Select a venue'}</div>
          )}
        </div>
      </div>

      {/* Selection action bar */}
      {selCount > 0 && (
        <div className="fixed bottom-3 inset-x-3 sm:left-auto sm:right-6 sm:w-[420px] z-40 bg-white border shadow-xl rounded-xl p-3" data-testid="slotcal-selection-bar">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {Object.entries(sel).map(([ct, r]) => (
              <span key={ct} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-full px-2 py-0.5 text-[11px]">
                <b>{ct}</b> {minToLabel(slotMin(r.start))}–{minToLabel(slotMin(r.end) + STEP)} · ₹{rangePrice(ct, r)}
                <button onClick={() => setSel((p) => { const n = { ...p }; delete n[ct]; return n; })} className="hover:text-red-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold flex items-center"><IndianRupee className="w-4 h-4" />{totalPrice}</span>
            <Button size="sm" className="ml-auto bg-emerald-600 hover:bg-emerald-700 h-9" onClick={openBook} data-testid="slotcal-book-btn">Book</Button>
            <Button size="sm" variant="outline" className="h-9" onClick={() => setBlockOpen(true)} data-testid="slotcal-block-btn"><Ban className="w-3.5 h-3.5 mr-1" />Block</Button>
            <Button size="sm" variant="ghost" className="h-9" onClick={() => setSel({})} data-testid="slotcal-clear-btn">Clear</Button>
          </div>
        </div>
      )}

      {/* Book dialog */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="max-w-md" data-testid="slotcal-book-dialog">
          <DialogHeader>
            <DialogTitle>New Booking — {format(new Date(date + 'T00:00:00'), 'd MMM yyyy')}</DialogTitle>
            <DialogDescription className="sr-only">Create bookings for the selected slots</DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-600 space-y-1">
            {Object.entries(sel).map(([ct, r]) => {
              const courtObj = cal?.courts.find((c) => c.court === ct);
              const courtGames = courtObj?.games || [];
              const multi = courtGames.length > 1;
              return (
                <div key={ct} className="flex items-center gap-2 flex-wrap" data-testid={`slotcal-book-court-row-${ct}`}>
                  <span className="min-w-0"><b>{ct}</b> · {minToLabel(slotMin(r.start))} – {minToLabel(slotMin(r.end) + STEP)} · ₹{rangePrice(ct, r)}</span>
                  {multi ? (
                    <Select
                      value={bookGames[ct] || courtGames[0] || ''}
                      onValueChange={(v) => setBookGames((prev) => ({ ...prev, [ct]: v }))}
                    >
                      <SelectTrigger className="h-7 w-[130px] ml-auto" data-testid={`slotcal-book-game-${ct}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {courtGames.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span
                      className="ml-auto text-[11px] font-medium bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-slate-700"
                      data-testid={`slotcal-book-game-readonly-${ct}`}
                    >
                      {bookGames[ct] || courtGames[0] || '—'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 relative">
              <Label className="text-xs">Player Name *</Label>
              <Input
                value={bookForm.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Type 2+ letters to find existing player"
                data-testid="slotcal-book-name"
                autoFocus
              />
              {suggestions.length > 0 && (
                <div className="absolute z-50 inset-x-0 top-full mt-1 bg-white border rounded-lg shadow-lg max-h-44 overflow-auto" data-testid="slotcal-player-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.mobile}
                      type="button"
                      data-testid={`slotcal-suggestion-${s.mobile}`}
                      onClick={() => { setBookForm((f) => ({ ...f, mobile: s.mobile, name: s.name })); setLastBooking(s.last_booking || null); setSuggestions([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between gap-2 border-b last:border-b-0"
                    >
                      <span className="text-sm font-medium truncate">{s.name}</span>
                      <span className="text-xs text-slate-500 shrink-0">{s.mobile}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2 relative">
              <Label className="text-xs">Mobile Number</Label>
              <Input
                value={bookForm.mobile}
                onChange={(e) => handleMobileChange(e.target.value)}
                placeholder="Type 4+ digits to find existing player"
                inputMode="numeric"
                data-testid="slotcal-book-mobile"
              />
              {lastBooking && (
                <div className="text-[11px] text-emerald-700 mt-1" data-testid="slotcal-last-booking">
                  Last booking: {lastBooking.game} · {lastBooking.date ? format(new Date(lastBooking.date + 'T00:00:00'), 'd MMM yyyy') : '—'} · {lastBooking.court}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={bookForm.source} onValueChange={(v) => setBookForm({ ...bookForm, source: v })}>
                <SelectTrigger data-testid="slotcal-book-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payment Mode</Label>
              <Select value={bookForm.payMode} onValueChange={(v) => setBookForm({ ...bookForm, payMode: v })}>
                <SelectTrigger data-testid="slotcal-book-paymode"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_MODES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input type="number" value={bookForm.amount} onChange={(e) => setBookForm({ ...bookForm, amount: e.target.value })} data-testid="slotcal-book-amount" />
            </div>
            <div>
              <Label className="text-xs">Advance (₹)</Label>
              <Input type="number" value={bookForm.advance} onChange={(e) => setBookForm({ ...bookForm, advance: e.target.value })} data-testid="slotcal-book-advance" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitBooking} disabled={saving} data-testid="slotcal-book-submit">
              {saving ? 'Booking…' : `Confirm ${selCount > 1 ? `${selCount} Courts` : 'Booking'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block dialog */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="max-w-sm" data-testid="slotcal-block-dialog">
          <DialogHeader><DialogTitle>Block Slots — {format(new Date(date + 'T00:00:00'), 'd MMM yyyy')}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-600 space-y-0.5">
            {Object.entries(sel).map(([ct, r]) => (
              <div key={ct}><b>{ct}</b> · {minToLabel(slotMin(r.start))} – {minToLabel(slotMin(r.end) + STEP)}</div>
            ))}
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={blockForm.notes} onChange={(e) => setBlockForm({ ...blockForm, notes: e.target.value })} data-testid="slotcal-block-notes" autoFocus />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={blockForm.reason} onValueChange={(v) => setBlockForm({ ...blockForm, reason: v })}>
                <SelectTrigger data-testid="slotcal-block-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{BLOCK_REASONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submitBlock} disabled={saving} data-testid="slotcal-block-submit">{saving ? 'Blocking…' : 'Block Slots'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog (booking / block) */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-sm" data-testid="slotcal-detail-dialog">
          <DialogDescription className="sr-only">Slot details and actions</DialogDescription>
          {detail?.type === 'booking' && (() => {
            const b = detail.data;
            return (
              <>
                <DialogHeader><DialogTitle className="flex items-center gap-2">{b.player_name}</DialogTitle></DialogHeader>
                <div className="text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-slate-500">Time</span><b>{minToLabel(b.start_min)} – {minToLabel(b.end_min)}</b></div>
                  <div className="flex justify-between"><span className="text-slate-500">Court</span><span>{b.court} · {b.game}</span></div>
                  {b.player_mobile && b.player_mobile !== 'N/A' && (
                    <div className="flex justify-between"><span className="text-slate-500">Mobile</span><a href={`tel:${b.player_mobile}`} className="text-emerald-700 font-medium">{b.player_mobile}</a></div>
                  )}
                  <div className="flex justify-between"><span className="text-slate-500">Source</span><span>{b.source || '—'}</span></div>
                  {b.booked_by && <div className="flex justify-between"><span className="text-slate-500">Booked by</span><span>{b.booked_by}</span></div>}
                  <div className="flex justify-between"><span className="text-slate-500">Amount</span><span>₹{b.amount}{b.pending > 0 ? <span className="text-amber-600 text-xs"> (₹{b.pending} pending)</span> : ''}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="text-xs">{b.booking_id}</span></div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs text-slate-500">Duration</span>
                  <Button size="sm" variant="outline" className="h-8 px-2" disabled={saving} onClick={() => resizeBooking(b, -STEP)} data-testid="slotcal-shrink-btn"><Minus className="w-3.5 h-3.5" />30m</Button>
                  <Button size="sm" variant="outline" className="h-8 px-2" disabled={saving} onClick={() => resizeBooking(b, STEP)} data-testid="slotcal-extend-btn"><Plus className="w-3.5 h-3.5" />30m</Button>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => { setMoveBooking(b); setDetail(null); setSel({}); }} data-testid="slotcal-move-btn"><Move className="w-3.5 h-3.5 mr-1" />Move</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/office/bookings?edit=${b.id}`)} data-testid="slotcal-edit-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                  <Button size="sm" variant="destructive" disabled={saving} onClick={() => cancelBooking(b)} data-testid="slotcal-cancel-booking-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                </div>
              </>
            );
          })()}
          {detail?.type === 'block' && (() => {
            const blk = detail.data;
            return (
              <>
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="w-4 h-4" />Blocked — {blk.reason}</DialogTitle></DialogHeader>
                <div className="text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-slate-500">Time</span><b>{minToLabel(blk.start_min)} – {minToLabel(blk.end_min)}</b></div>
                  <div className="flex justify-between"><span className="text-slate-500">Court</span><span>{blk.court}</span></div>
                  {blk.blocked_by && <div className="flex justify-between"><span className="text-slate-500">Blocked by</span><span>{blk.blocked_by}</span></div>}
                  {blk.notes && <div className="text-xs text-slate-500">{blk.notes}</div>}
                  {blk.recurring && <div className="text-xs text-amber-600">Recurring / bulk booking. Freeing this slot cancels only today's occurrence; other days stay booked.</div>}
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  {blk.recurring ? (
                    <>
                      <Button size="sm" variant="destructive" disabled={saving} onClick={() => cancelFreeSlot(blk)} data-testid="slotcal-free-slot-btn">
                        <Trash2 className="w-3.5 h-3.5 mr-1" />Cancel / Free This Slot
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate('/office/slot-inventory')} data-testid="slotcal-manage-block-btn">Manage Bulk Booking</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { setEditBlock({ ...blk }); setDetail(null); }} data-testid="slotcal-edit-block-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                      <Button size="sm" variant="destructive" disabled={saving} onClick={() => deleteBlock(blk)} data-testid="slotcal-delete-block-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Remove Block</Button>
                    </>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit block dialog */}
      <Dialog open={!!editBlock} onOpenChange={(o) => !o && setEditBlock(null)}>
        <DialogContent className="max-w-sm" data-testid="slotcal-edit-block-dialog">
          {editBlock && (
            <>
              <DialogHeader><DialogTitle>Edit Block — {editBlock.court}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={editBlock.notes} onChange={(e) => setEditBlock({ ...editBlock, notes: e.target.value })} data-testid="slotcal-edit-block-notes" />
                </div>
                <div>
                  <Label className="text-xs">Reason</Label>
                  <Select value={editBlock.reason} onValueChange={(v) => setEditBlock({ ...editBlock, reason: v })}>
                    <SelectTrigger data-testid="slotcal-edit-block-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(BLOCK_REASONS.includes(editBlock.reason) ? BLOCK_REASONS : [editBlock.reason, ...BLOCK_REASONS]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Start Time</Label>
                    <Select value={String(editBlock.start_min)} onValueChange={(v) => setEditBlock({ ...editBlock, start_min: parseInt(v, 10) })}>
                      <SelectTrigger data-testid="slotcal-edit-block-start"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {timeOptions.slice(0, -1).map((m) => <SelectItem key={m} value={String(m)}>{minToLabel(m)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">End Time</Label>
                    <Select value={String(editBlock.end_min)} onValueChange={(v) => setEditBlock({ ...editBlock, end_min: parseInt(v, 10) })}>
                      <SelectTrigger data-testid="slotcal-edit-block-end"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {timeOptions.filter((m) => m > editBlock.start_min).map((m) => <SelectItem key={m} value={String(m)}>{minToLabel(m)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditBlock(null)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={saveBlockEdit} data-testid="slotcal-edit-block-save">{saving ? 'Saving…' : 'Save Changes'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
